"use client";

import dynamic from "next/dynamic";
import DepthPaywall from "@/components/DepthPaywall";
import OfferImpression from "@/components/OfferImpression";
import ReportCollectInputsCTA from "@/components/ReportCollectInputsCTA";
import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";
import type { PremiumReportPlanId } from "@ishvaram/domain-data/report-plans";

const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});

/**
 * Intent-matched paid-report offer for calculators / reference pages whose matched
 * upgrade is a dedicated digital report. Reference surfaces bridge to the birth-
 * details form; calculators that already hold the backend-required birth fields
 * open checkout directly. Both paths share one attribution and impression contract.
 *
 * Price is read from the canonical generated plan map the Razorpay money path uses,
 * so it cannot drift from what is charged. PaidReportCTA remains a lazy chunk so
 * reference pages do not pay its checkout cost before a value moment exists.
 *
 * Reach is metered by the render-once `OfferImpression` island (rule 46/47):
 * `PaidReportCTA`'s own mount effect is short-circuited in acquisition mode
 * (`collectInputsHref` set), so without this island the offer fires ZERO
 * `agent_revenue_offer_shown`. The island keys on the stable plan/placement so it
 * fires exactly once (rule 47), matching the click events.
 *
 * Must be listed in `ux_surface_budgets.yaml` monetization_components so the
 * budget guard counts it (single primary offer per surface).
 *
 * Copy is pain-led per rule 22 in rule-21 vocabulary; the caller passes the
 * matched headline/subhead for its report type.
 */
export default function CalculatorReportOfferCTA({
  planId,
  language = "en",
  sourcePath,
  placement,
  customerProblem,
  headline,
  subhead,
  name,
  birthDate,
  birthTime,
  birthPlace,
}: {
  planId: PremiumReportPlanId;
  language?: "en" | "hi";
  /** The page the offer is shown on (for accurate attribution). */
  sourcePath: string;
  /** Stable placement tag for the impression + click events. */
  placement: string;
  /** Short customer-problem tag (e.g. "gemstone", "baby_name", "lal_kitab"). */
  customerProblem: string;
  headline: string;
  subhead: string;
  name?: string;
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
}) {
  const report = reportPlanSummaryMap[planId];
  const birth = { name, birthDate, birthTime, birthPlace };
  const reportInputs = paidReportInputs(birth, language);
  const collectInputsHref = reportInputHref({
    sourcePath,
    planId: report.id,
    language,
    birth,
  });
  const routeRankReason = `${customerProblem}_lookup_${report.id}`;
  const analyticsContext = {
    checkoutChannel: "ishvaram_report" as const,
    commerceSystem: "ishvaram" as const,
    intent: "chart_report",
    sourcePath,
    placement,
    customerProblem,
    urgency: "medium",
    confidence: 0.8,
    routeRankScore: 90,
    routeRankReason,
    rankedOfferOrder: [report.id],
  };

  return (
    <div className="mx-auto my-8 max-w-md px-4 sm:px-6">
      <OfferImpression
        planId={report.id}
        actionMode={reportInputs ? "direct_checkout" : "input_collection"}
        sourcePath={sourcePath}
        intent="chart_report"
        placement={placement}
        customerProblem={customerProblem}
        urgency="medium"
        confidence={0.8}
        routeRankScore={90}
        routeRankReason={routeRankReason}
        rankedOfferOrder={[report.id]}
      />
      <DepthPaywall planId={report.id} language={language}>
        {reportInputs ? (
          <PaidReportCTA
            planId={report.id}
            priceInr={report.price_inr}
            headline={headline}
            subhead={subhead}
            language={language}
            source={sourcePath.replace(/^\//, "")}
            reportInputs={reportInputs}
            analyticsContext={analyticsContext}
          />
        ) : (
          <ReportCollectInputsCTA
            planId={report.id}
            priceInr={report.price_inr}
            headline={headline}
            subhead={subhead}
            language={language}
            collectInputsHref={collectInputsHref}
            collectInputsLabel={language === "hi" ? "Janam details bharein" : "Enter birth details"}
            collectInputsNote={collectInputsNote(language, Boolean(birthDate))}
            analyticsContext={analyticsContext}
          />
        )}
      </DepthPaywall>
    </div>
  );
}

type BirthInputs = {
  name?: string;
  birthDate?: string;
  birthTime?: string;
  birthPlace?: string;
};

function paidReportInputs(
  { name, birthDate, birthTime, birthPlace }: BirthInputs,
  language: "en" | "hi"
): Record<string, unknown> | null {
  const cleanName = name?.trim();
  const cleanPlace = birthPlace?.trim();
  if (!cleanName || !birthDate || !cleanPlace) return null;
  return {
    name: cleanName,
    birth_date: birthDate,
    birth_time: birthTime || null,
    birth_place: cleanPlace,
    language,
  };
}

function reportInputHref({
  sourcePath,
  planId,
  language,
  birth,
}: {
  sourcePath: string;
  planId: string;
  language: "en" | "hi";
  birth: BirthInputs;
}): string {
  const params: Record<string, string> = {
    intent: "chart_report",
    source: sourcePath,
    next_report: planId,
  };
  if (birth.name?.trim()) params.name = birth.name.trim();
  if (birth.birthDate) params.dob = birth.birthDate;
  if (birth.birthTime) params.time = birth.birthTime;
  if (birth.birthPlace?.trim()) params.place = birth.birthPlace.trim();
  const base = language === "hi" ? "/hi/free-kundali/" : "/free-kundali/";
  return `${base}#${new URLSearchParams(params).toString()}`;
}

function collectInputsNote(language: "en" | "hi", carriesDob: boolean): string {
  if (carriesDob) {
    return language === "hi"
      ? "Aapki bhari hui birth details carry over ho jaayengi — confirm karein, checkout khul jaayega."
      : "The birth details you entered carry over — confirm them and checkout opens.";
  }
  return language === "hi"
    ? "Pehle birth details bharein. Chart ready hote hi checkout khulega."
    : "Birth details come first. Checkout opens after the chart has the inputs needed for your report.";
}
